/* eslint-disable */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/pro-regular-svg-icons/faUpload";
import { faPlusCircle } from "@fortawesome/pro-regular-svg-icons/faPlusCircle";
import { faDownload } from "@fortawesome/pro-regular-svg-icons/faDownload";

import classNames from "classnames";
import { Helmet } from "react-helmet";
import axios from "axios";
import { toast } from "react-toastify";

import {
  HydraTable,
  Heading,
  TextFilter,
  DateFilter,
  SelectFilter,
  StyledCheckbox,
} from "@peracto/peracto-ui";

import { useConfig } from "@peracto/peracto-config";
import { GET_LIST, CREATE, useClient } from "@peracto/client";

const PromotionsList = () => {
  // Default visible columns - key = column accessor
  const [selectedColumns, onSelectColumn] = useState({
    startDateTime: true,
    endDateTime: true,
  });

  const { client } = useClient();
  const options = { year: "numeric", month: "long", day: "numeric" };

  const config = useConfig();
  const formatDate = config.get("user_format_date", config.get("format_date"));
  const API_URL = config.get("api");

  // The 'selectable' property is used to define if the
  // column is available in the 'manage columns' dropdown
  const columns = [
    {
      id: "selection",
      Header: ({ getToggleAllRowsSelectedProps }) => (
        <StyledCheckbox {...getToggleAllRowsSelectedProps()} />
      ),
      Cell: ({ row }) => (
        <StyledCheckbox {...row.getToggleRowSelectedProps()} />
      ),
      selectable: false,
    },

    {
      Header: "Name",
      accessor: "name",
      sortType: "basic",
      Filter: (props) => <TextFilter label="Name" {...props} />,
      Cell: (row) => {
        return (
          <Link to={row.row.original.id}>
            {row.row.original.promotion.name}
          </Link>
        );
      },
      selectable: false,
    },
    {
      Header: "Status",
      accessor: "status",
      sortType: "basic",
      Filter: (props) => (
        <SelectFilter
          label="Status"
          options={[
            { value: "active", label: "Active" },
            { value: "archived", label: "Archived" },
          ]}
          {...props}
        />
      ),
      Cell: (row) => {
        const value = row.row.original.promotion.status;
        return (
          <span
            className={classNames("badge", "badge-pill", "text-capitalize", {
              "badge-success": value == "active",
              "badge-danger": value == "archived",
            })}
          >
            {value}
          </span>
        );
      },
      selectable: false,
    },
    {
      Header: "Order Count",

      Cell: (row) => {
        const value = row.row.original.metrics[0].orderCount;
        return <>{value}</>;
      },
      selectable: false,
    },
    {
      Header: "User Count",
      Cell: (row) => {
        const value = row.row.original.metrics[0].userCount;
        return <>{value}</>;
      },
    },
    {
      Header: "Start Date",
      accessor: "startDateTime",
      sortType: "basic",
      Filter: (props) => <DateFilter label="Start Date" {...props} />,
      Cell: (row) => {
        const value = row.row.original.promotion.startDateTime;
        return value
          ? new Date(value).toLocaleDateString(undefined, options)
          : "-";
      },

      show: selectedColumns["startDateTime"],
      selectable: true,
    },
    {
      Header: "End Date",
      accessor: "endDateTime",
      sortType: "basic",
      Filter: (props) => <DateFilter label="End Date" {...props} />,
      Cell: (row) => {
        const value = row.row.original.promotion.endDateTime;
        return value
          ? new Date(value).toLocaleDateString(undefined, options)
          : "-";
      },
      show: selectedColumns["endDateTime"],
      selectable: true,
    },
  ];

  const onFetchData = (options) =>
    client(GET_LIST, "promotions", options).then((result) => ({
      ...result,
      data: result.data.map((row, index) => ({
        ...row,
        originId: parseInt(row.id?.split("/").pop()) || index,
      })),
    }));

  const handleChange = async (e) => {
    const file = e.currentTarget.files[0];

    if (!file) return;

    const reader = new FileReader();

    reader.readAsDataURL(file);

    // Create FormData. API is expecting `{file: file}` as form-data.
    const formData = new FormData();
    formData.append("file", file);
    formData.append("importType", "promotion_codes");
    formData.append("sourceType", "csv");

    try {
      const response = await axios.post(`${API_URL}/imports`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const { errors = [], successfulCount = 0, errorCount = 0, totalCount = 0 } = response?.data || {};

      if (errors.length > 0) {
        const error = errors[0];
        if (error.type === "invalid_columns" && error.extra_data?.unrecognised_columns) {
          const invalid = error.extra_data.unrecognised_columns.join(", ");
          const expected = error.extra_data?.available_columns?.join(", ");
          toast.error(
            `Invalid CSV columns: ${invalid}.${expected ? ` Expected: ${expected}` : ""}`
          );
        } else {
          toast.error(error.message || "Whoops, there was a problem...");
        }
      } else if (successfulCount === 0 && errorCount > 0) {
        toast.error(`Upload failed: all ${errorCount} rows could not be processed.`);
      } else if (errorCount > 0) {
        toast.warn(`${successfulCount} of ${totalCount} codes uploaded. ${errorCount} rows failed.`);
      } else {
        toast.success("Promotion codes successfully uploaded!");
      }
    } catch (error) {
      const { response } = error;
      console.log("error:", response);
      toast.error(
        error?.error?.body?.hasOwnProperty("hydra:description")
          ? error.error.body["hydra:description"]
          : "Whoops, there was a problem..."
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>Promotions | Peracto</title>
      </Helmet>
      <Heading name="Promotions">
        <div className="ml-2 d-flex align-items-center">
          <a
            className="mr-2 btn btn-outline-secondary"
            href="/exampleFiles/promotion_codes_import_example.csv"
            download="promotion_codes_import_example.csv"
          >
            <FontAwesomeIcon icon={faDownload} className="mr-2" />
            Download Sample CSV
          </a>

          <div className="overflow-hidden position-relative">
            <input
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                opacity: 0,
                cursor: "pointer",
              }}
              type="file"
              id="promoUpload"
              onChange={(evt) => {
                handleChange(evt);
              }}
              accept=".csv"
            />
            <button className="mr-2 btn btn-outline-primary">
              <FontAwesomeIcon icon={faUpload} className="mr-2" />
              Upload Promotion Codes
            </button>
          </div>

          <Link
            className="btn btn-primary"
            to="/promotions/add"
            data-testid="add-promotion"
          >
            <FontAwesomeIcon icon={faPlusCircle} className="mr-2" />
            Add Promotion
          </Link>
        </div>
      </Heading>

      <div className="row">
        <div className="col">
          <div className="card card--transparent">
            <div className="card-body">
              <HydraTable
                columns={columns}
                selectedColumns={selectedColumns}
                onFetchData={onFetchData}
                onSelectColumn={onSelectColumn}
                defaultFilters={{
                  name: "",
                  status: "active",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PromotionsList;
